import { Asset, Button, Modal, Pill, PlusIcon } from '@clearcover/atlas';
import {
  DriverType,
  ExcludedDriverType,
  VehicleType,
} from '@clearcover/core-app-types';
import React, { FC, useContext, useEffect } from 'react';

import { Card } from 'Shared/components/Card';
import { CardDataSection } from 'Shared/components/Card/CardDataSection';
import { CardSectionInfoPairs } from 'Shared/components/Card/CardSectionInfoPairs';
import { ExpansionPanel } from 'Shared/components/ExpansionPanel';
import { combineStrings, formatDateShort } from 'Shared/utils';
import styles from './styles.module.css';

import {
  addDriverPermissionsPolicy,
  editDriverPermissionsPolicy,
  removeDriverPermissionsPolicy,
} from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { CardHeader } from 'Shared/components';
import { useModalHashChange } from 'Shared/hooks';
import { ConflictingDraftModal } from '../ConflictingDraftModal';
import { PolicyDriverDetails } from '../PolicyDriverDetails/PolicyDriverDetails';
import { DriverForm, DriverOptionsResponse } from './DriverForm';
import { EditDriverButtons, RemoveDriverButton } from './EditDriverButtons';

export type PolicyDriversSectionType = {
  drivers: Array<DriverType>;
  excludedDrivers: Array<ExcludedDriverType>;
  vehicles: Array<VehicleType>;
};

export const PolicyDriversSection: FC<PolicyDriversSectionType> = ({
  drivers,
  excludedDrivers,
  vehicles,
}) => {
  const {
    details,
    isEndorseablePolicy,
    resourceEffectiveGrants,
    canEndorseDuringRegionalEvents,
    setDriverOptions,
  } = useContext(PolicyContext);

  const [{ data, loading: isLoading }] = useNewApiV2<DriverOptionsResponse>({
    url: `/options/driver?configDiscoveryParams=${details.configDiscoveryParams}&productFilingIdentifier=${details.filingIdentifier}`,
  });

  useEffect(() => {
    if (data) {
      setDriverOptions(data);
    }
  }, [data, setDriverOptions]);

  const driverInfoOptions = data?.info;

  const { modalHashChange } = useModalHashChange('addDriver');

  const canAddDriver =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    addDriverPermissionsPolicy(resourceEffectiveGrants);

  const showEditDriverButtons =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    (removeDriverPermissionsPolicy(resourceEffectiveGrants) ||
      editDriverPermissionsPolicy(resourceEffectiveGrants));

  const showRemoveExcludedDriverButton =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    removeDriverPermissionsPolicy(resourceEffectiveGrants);

  return (
    <Card id="drivers-card">
      <CardHeader title={'Drivers'}>
        {canAddDriver && (
          <Modal
            onVisibilityChange={modalHashChange}
            variant={'full'}
            toggle={(openAddDriverModal) => (
              <ConflictingDraftModal
                onApprove={openAddDriverModal}
                renderToggle={(openModal) => (
                  <Button
                    className={styles.addDriverButton}
                    size="sm"
                    variant="secondary"
                    onClick={openModal}
                    data-testid="open-add-driver-modal"
                  >
                    <Asset
                      icon={PlusIcon}
                      description="Add Component"
                      height={10}
                      width={10}
                      className={styles.icon}
                    />{' '}
                    Add
                  </Button>
                )}
              />
            )}
          >
            {(close) => (
              <DriverForm type="create" close={close} vehicles={vehicles} />
            )}
          </Modal>
        )}
      </CardHeader>
      {drivers.map((driver, index) => {
        const {
          id,
          person: {
            name: { firstName, lastName, middleName },
            suffix,
          },
        } = driver;
        return (
          <ExpansionPanel
            id={'driver-' + index}
            key={id}
            head={
              <div
                className={styles.title}
                data-testid="driver-expansion-panel"
              >
                <div className={styles.cardHeader}>
                  <div>
                    <span className="fs-exclude">
                      {combineStrings([
                        firstName,
                        middleName,
                        lastName,
                        suffix,
                      ])}
                    </span>
                    {driver.relationToApplicant === 'applicant' && (
                      <Pill variant={'primary'} className={styles.pill}>
                        Primary
                      </Pill>
                    )}
                  </div>
                  <div>
                    {showEditDriverButtons && (
                      <EditDriverButtons
                        driver={driver}
                        vehicles={vehicles}
                        withBackground={false}
                        disableRemoveButton={
                          driver.policyPartyRelationship === 'named_insured'
                        }
                        removeButtonTooltip={
                          driver.policyPartyRelationship === 'named_insured'
                            ? 'You cannot remove the primary insured from the policy.'
                            : ''
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            }
          >
            <PolicyDriverDetails
              data-testid="driver-details"
              driver={driver}
              vehicles={vehicles}
              driverInfoOptions={driverInfoOptions}
            />

            {showEditDriverButtons && (
              <EditDriverButtons
                driver={driver}
                vehicles={vehicles}
                withBackground={true}
                disableRemoveButton={
                  driver.policyPartyRelationship === 'named_insured'
                }
                removeButtonTooltip={
                  driver.policyPartyRelationship === 'named_insured'
                    ? 'You cannot remove the primary insured from the policy.'
                    : ''
                }
              />
            )}
          </ExpansionPanel>
        );
      })}
      {excludedDrivers
        .sort((a, b) =>
          a.policyPartyRelationship > b.policyPartyRelationship ? -1 : 1
        )
        .map((driver, index) => {
          const {
            id,
            person: {
              name: { firstName, lastName, middleName, suffix },
              dateOfBirth,
              gender,
              maritalStatus,
            },
            policyPartyRelationship,
            relationshipToApplicant,
          } = driver;
          const isListOnly = policyPartyRelationship === 'listed_person';
          const identifier = `${isListOnly ? 'list-only' : 'excluded'}-driver`;
          return (
            <ExpansionPanel
              id={`${identifier}-${index}`}
              key={id}
              head={
                <div
                  className={styles.title}
                  data-testid={`${identifier}-expansion-panel`}
                >
                  <div className={styles.cardHeader}>
                    <div>
                      <span>
                        {combineStrings([firstName, middleName, lastName])}
                      </span>
                      <Pill variant="disabled" className={styles.pill}>
                        {isListOnly ? 'List Only' : 'Excluded'}
                      </Pill>
                    </div>
                    <div>
                      {showRemoveExcludedDriverButton && (
                        <RemoveDriverButton
                          driver={driver}
                          useStyle={true}
                          withBackground={false}
                        />
                      )}
                    </div>
                  </div>
                </div>
              }
            >
              <CardDataSection caption={'General Info'} lastSection={true}>
                <CardSectionInfoPairs
                  loading={isLoading}
                  headers={[
                    'FIRST NAME',
                    'MIDDLE INITIAL',
                    'LAST NAME',
                    'SUFFIX',
                    'DATE OF BIRTH',
                    ...(details.showExcludedDriverGender ? ['GENDER'] : []),
                    ...(details.showExcludedDriverMaritalStatus
                      ? ['MARITAL STATUS']
                      : []),
                    'RELATION TO APPLICANT',
                  ]}
                  data={[
                    firstName,
                    middleName,
                    lastName,
                    suffix,
                    formatDateShort(dateOfBirth),
                    ...(details.showExcludedDriverGender
                      ? [
                          driverInfoOptions?.gender?.[gender as string] ??
                            gender ??
                            '—',
                        ]
                      : []),
                    ...(details.showExcludedDriverMaritalStatus
                      ? [
                          driverInfoOptions?.maritalStatus?.[
                            maritalStatus as string
                          ] ??
                            maritalStatus ??
                            '—',
                        ]
                      : []),
                    driverInfoOptions?.relationToApplicant?.[
                      relationshipToApplicant as string
                    ] ??
                      relationshipToApplicant ??
                      '—',
                  ]}
                />
              </CardDataSection>
              {showRemoveExcludedDriverButton && (
                <RemoveDriverButton
                  driver={driver}
                  useStyle={true}
                  withBackground={true}
                />
              )}
            </ExpansionPanel>
          );
        })}
    </Card>
  );
};
